<script setup lang="ts">
import { computed } from 'vue'
import FairCheckboxIndicator from './FairCheckboxIndicator.vue'
import type { FairCheckboxPreviewState } from '@/ui/tokens/checkbox.data'

interface FairCheckboxProps {
  modelValue?: boolean
  label?: string
  showLabel?: boolean
  disabled?: boolean
  inputId?: string
  name?: string
  value?: string | number | boolean
  ariaLabel?: string
  previewState?: FairCheckboxPreviewState
  interactive?: boolean
}

const props = withDefaults(defineProps<FairCheckboxProps>(), {
  modelValue: false,
  label: 'Label',
  showLabel: true,
  disabled: false,
  inputId: undefined,
  name: undefined,
  value: undefined,
  ariaLabel: undefined,
  previewState: undefined,
  interactive: true,
})

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  change: [value: boolean]
}>()

const isChecked = computed(() => props.modelValue)

const isDisabled = computed(() =>
  props.disabled || props.previewState === 'disabled',
)

const rootClass = computed(() => ({
  'fair-checkbox': true,
  'fair-checkbox--checked': isChecked.value,
  'fair-checkbox--disabled': isDisabled.value,
  'fair-checkbox--non-interactive': !props.interactive,
  [`fair-checkbox--preview-${props.previewState}`]: Boolean(props.previewState),
}))

function handleChange(event: Event) {
  if (!props.interactive || isDisabled.value) {
    return
  }

  const target = event.target as HTMLInputElement
  emit('update:modelValue', target.checked)
  emit('change', target.checked)
}
</script>

<template>
  <label :class="rootClass">
    <input
      :id="inputId"
      type="checkbox"
      class="fair-checkbox__input"
      :checked="isChecked"
      :disabled="isDisabled"
      :name="name"
      :value="value"
      :aria-label="showLabel ? undefined : ariaLabel ?? label"
      @change="handleChange"
    >
    <FairCheckboxIndicator
      :checked="isChecked"
      :disabled="isDisabled"
      :preview-state="previewState"
    />
    <span
      v-if="showLabel"
      class="fair-checkbox__label"
    >
      <slot>{{ label }}</slot>
    </span>
  </label>
</template>
